// =============================================================================
// components/KpiIncidents/IncidentFilters.jsx
//
// Dense filter toolbar — minimalist, no heavy header.
//
// FIX5 — nothing loads automatically anymore. Choosing a filter here only
// updates local state; the operator must click "Apply" (wired to
// IncidentDashboard.handleApply) to actually fetch. This avoids firing a
// request per keystroke/selection against a table with tens of thousands
// of rows.
//
// Tech/KPI/Vendor dropdown OPTIONS come from `lookups` (GetLookupsAsync —
// the full, authoritative list of active detection rules and vendors,
// independent of Status or of which incidents currently exist) — NOT from
// the incidents themselves, which are only ever the current filtered page.
// KPI options cascade by the selected Tech, using each lookup KPI's own
// `tech` field.
//
// FIX: selecting Status = "Closed" shows a bottom-right snackbar informing
// the operator that closed incidents older than 30 days are automatically
// purged (see TKAD_SP_Purge_Closed_Incidents). Only fires on the actual
// selection of "Closed", not on every render.
// =============================================================================
import { useMemo, useState } from 'react';
import {
	Box, Paper, FormControl, InputLabel, Select, MenuItem,
	TextField, Button, Chip, Tooltip, InputAdornment, IconButton,
	Snackbar, Alert,
} from '@mui/material';
import FilterAltOffIcon  from '@mui/icons-material/FilterAltOff';
import SearchIcon        from '@mui/icons-material/Search';
import {
	STATUS_OPTIONS, SEVERITY_OPTIONS, DURATION_OPTIONS,
} from '../../constants/kpiConstants';

function countActiveFilters(filters) {
	let n = 0;
	if (filters.status   !== 'Open') n += 1;
	if (filters.tech     !== 'all')  n += 1;
	if (filters.severity !== 'all')  n += 1;
	if (filters.vendor   !== 'all')  n += 1;
	if (filters.kpiName  !== 'all')  n += 1;
	if (filters.siteId   !== '')     n += 1;
	if (filters.cellId   !== '')     n += 1;
	if (filters.durationBucket !== 'all') n += 1;
	return n;
}

export default function IncidentFilters({
	filters, lookups,
	onChange, onReset, onApply, loading,
}) {
	const activeCount = countActiveFilters(filters);
	const hasActive   = activeCount > 0;

	const [showRetentionNotice, setShowRetentionNotice] = useState(false);

	// Full, authoritative option lists — independent of the current Status
	// filter (see the FIX note above). Techs and Vendors are already
	// distinct/sorted server-side; KPI names cascade by the selected Tech.
	const techOptions = (lookups && lookups.techs) || [];
	const vendorOptions = (lookups && lookups.vendors) || [];

	const kpiOptions = useMemo(function () {
		const allKpis = (lookups && lookups.kpis) || [];
		const scoped = (filters.tech && filters.tech !== 'all')
			? allKpis.filter(function (k) { return k.tech === filters.tech; })
			: allKpis;
		const names = Array.from(new Set(scoped.map(function (k) { return k.kpiName; }).filter(Boolean)));
		names.sort();
		return names;
	}, [lookups, filters.tech]);

	const handle = function (field) {
		return function (e) {
			let value = e.target.value;
			if (field === 'severity' && value !== 'all') value = Number(value);

			if (field === 'status' && value === 'Closed') {
				setShowRetentionNotice(true);
			}

			const patch = {};
			patch[field] = value;

			// FIX: KPI names are tech-specific. Changing Tech without
			// resetting KPI could leave an impossible combination selected
			// (e.g. tech=4G + a KPI that only exists under 2G) — the table
			// would silently go empty AND the KPI <Select> would hold a
			// value no longer present in its own (now Tech-filtered)
			// option list, which MUI reports as "out of range" and renders
			// inconsistently. Resetting KPI to "all" whenever Tech changes
			// keeps the pair always valid.
			if (field === 'tech') {
				patch.kpiName = 'all';
			}

			onChange(patch);
		};
	};

	const handleCloseRetentionNotice = function (event, reason) {
		if (reason === 'clickaway') return;
		setShowRetentionNotice(false);
	};

	return (
		<Paper variant="outlined" sx={{ p: 1.25 }}>
			<Box sx={{
				display: 'flex', alignItems: 'center',
				gap: 1, flexWrap: 'wrap',
			}}>
				{/* Status */}
				<FormControl size="small" sx={{ minWidth: 110 }}>
					<InputLabel>Status</InputLabel>
					<Select value={filters.status} label="Status" onChange={handle('status')}>
						{STATUS_OPTIONS.map(function (s) {
							return <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>;
						})}
					</Select>
				</FormControl>

				{/* Tech */}
				<FormControl size="small" sx={{ minWidth: 100 }}>
					<InputLabel>Tech</InputLabel>
					<Select value={filters.tech} label="Tech" onChange={handle('tech')}>
						<MenuItem value="all">All</MenuItem>
						{techOptions.map(function (t) {
							return <MenuItem key={t} value={t}>{t}</MenuItem>;
						})}
					</Select>
				</FormControl>

				{/* Severity */}
				<FormControl size="small" sx={{ minWidth: 125 }}>
					<InputLabel>Severity</InputLabel>
					<Select value={filters.severity} label="Severity" onChange={handle('severity')}>
						<MenuItem value="all">All</MenuItem>
						{SEVERITY_OPTIONS.map(function (s) {
							return (
								<MenuItem key={s.value} value={s.value}>
									<Box component="span" sx={{
										display: 'inline-block', width: 7, height: 7,
										borderRadius: '50%', bgcolor: s.color, mr: 1,
									}} />
									{s.label}
								</MenuItem>
							);
						})}
					</Select>
				</FormControl>

				{/* Duration */}
				<FormControl size="small" sx={{ minWidth: 145 }}>
					<InputLabel>Duration</InputLabel>
					<Select value={filters.durationBucket} label="Duration" onChange={handle('durationBucket')}>
						{DURATION_OPTIONS.map(function (d) {
							return <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>;
						})}
					</Select>
				</FormControl>

				{/* KPI (grows) */}
				<FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
					<InputLabel>KPI</InputLabel>
					<Select value={filters.kpiName} label="KPI" onChange={handle('kpiName')}>
						<MenuItem value="all">All KPIs</MenuItem>
						{kpiOptions.map(function (k) {
							return <MenuItem key={k} value={k}>{k}</MenuItem>;
						})}
					</Select>
				</FormControl>

				{/* Vendor */}
				<FormControl size="small" sx={{ minWidth: 125 }}>
					<InputLabel>Vendor</InputLabel>
					<Select value={filters.vendor} label="Vendor" onChange={handle('vendor')}>
						<MenuItem value="all">All</MenuItem>
						{vendorOptions.map(function (v) {
							return <MenuItem key={v} value={v}>{v}</MenuItem>;
						})}
					</Select>
				</FormControl>

				{/* Site search */}
				<TextField
					size="small"
					placeholder="Site ID"
					value={filters.siteId}
					onChange={handle('siteId')}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
							</InputAdornment>
						),
					}}
					sx={{ width: 140 }}
				/>

				{/* Cell search */}
				<TextField
					size="small"
					placeholder="Cell ID"
					value={filters.cellId}
					onChange={handle('cellId')}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
							</InputAdornment>
						),
					}}
					sx={{ width: 140 }}
				/>

				<Box sx={{ flex: 1 }} />

				{/* Active filters chip + reset */}
				{hasActive && (
					<>
						<Chip
							label={activeCount + ' filter' + (activeCount === 1 ? '' : 's')}
							size="small"
							color="primary"
							variant="outlined"
							sx={{ height: 22, fontSize: '0.7rem' }}
						/>
						<Tooltip title="Clear filters">
							<IconButton size="small" onClick={onReset}>
								<FilterAltOffIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</>
				)}

				<Button
					variant="contained"
					size="small"
					onClick={onApply}
					disabled={loading}
					sx={{ textTransform: 'none', fontWeight: 600 }}
				>
					Apply
				</Button>
			</Box>

			{/* Bottom-right notice: closed incidents are subject to retention
			    cleanup. Only shown right after the operator selects "Closed". */}
			<Snackbar
				open={showRetentionNotice}
				autoHideDuration={6000}
				onClose={handleCloseRetentionNotice}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
			>
				<Alert
					onClose={handleCloseRetentionNotice}
					severity="info"
					variant="filled"
					sx={{ maxWidth: 360 }}
				>
					Closed incidents older than 30 days are automatically deleted from the system.
				</Alert>
			</Snackbar>
		</Paper>
	);
}